import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { accountsSelectors } from 'app-components/flux/accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { getTransactionById } from 'app-components/flux/transactions/transactionsSelectors';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { useOpenInvoice } from 'containers/SalesAndBilling/invoiceHooks';
import { useAppSelector, useAppStore } from 'hooks/redux';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  link: {
    color: theme.palette.primary.main,
    maxWidth: 223,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

type OtherSideType = 'multiple-payments' | 'transfer' | 'unknown' | string;
const useOtherSideType = (
  coa?: ChartOfAccountProps | null,
  // Transfer is always set by QCS on AR transfers, so its safe to use.
  transferId?: string | null,
): OtherSideType => {
  const isCoaAr = useAppSelector((state) =>
    coa?.type === 'ACCOUNT' && coa?.id
      ? accountsSelectors.getAccountById(state, coa?.id)?.type === 'ACCOUNTS_RECEIVABLE'
      : false,
  );
  const arTxn = useAppSelector((state) => (transferId ? getTransactionById(state, transferId) : undefined));

  if (!isCoaAr) {
    return 'transfer';
  }

  if (arTxn?.allocations) {
    if ('length' in arTxn.allocations && arTxn.allocations.length >= 1) {
      return arTxn.allocations.length === 1 ? (arTxn.allocations[0].txnId ?? 'unknown') : 'multiple-payments';
    } else if ('size' in arTxn.allocations && arTxn.allocations.size >= 1)
      return arTxn.allocations.size === 1 ? (arTxn.allocations.get(0)?.txnId ?? 'unknown') : 'multiple-payments';
  }

  return 'unknown';
};

export const OtherSideField = React.memo(
  ({
    coa,
    transferId,
    txnId,
    altAmount,
    goToOtherSide,
    isSplit,
  }: {
    coa: ChartOfAccountProps | undefined | null;
    txnId: string | undefined | null;
    altAmount?: number;
    transferId?: string | null;
    goToOtherSide: (txn: TransactionProps) => void;
    isSplit?: boolean;
  }) => {
    const { classes } = useStyles();
    const store = useAppStore();
    const handleOpenInvoice = useOpenInvoice('details-other-side-field');
    const otherSideType = useOtherSideType(coa, transferId);

    if (otherSideType === 'unknown') {
      return null;
    }

    // Not all transfer txns guarantee transfer field. So need to use this function to find the transfer.
    const goToTransfer = () => {
      const txnList = transactionsSelectors.getTransactionsByAccountId(store.getState()).get(coa?.id ?? '');
      const initialTxn = txnId ? transactionsSelectors.getTransactionById(store.getState(), txnId) : undefined;
      const foundTxn =
        txnList && initialTxn ? transactionsUtils.findMatchingTransferTx(initialTxn, txnList, altAmount)?.txn : null;

      if (foundTxn) {
        goToOtherSide(foundTxn);
      } else {
        verify(false, 'Web - transfer other side missing');
      }
    };

    const viewInvoice = () => {
      const txn = transactionsSelectors.getTransactionById(store.getState(), otherSideType);
      if (txn) {
        handleOpenInvoice({ invoiceId: txn.id ?? '' });
      } else {
        verify(false, 'Web - invoice other side missing');
      }
    };

    const txn = transactionsSelectors.getTransactionById(store.getState(), otherSideType);
    const txnSubtype = txn?.subtype;
    const singularCopy = txnSubtype === 'INVOICE_CREDIT_MEMO' ? 'to a credit memo' : 'as an invoice payment';
    const viewCopy = txnSubtype === 'INVOICE_CREDIT_MEMO' ? 'View credit memo' : 'View invoice';
    const multipleCopy = txn && txn?.amount > 0 ? 'as multiple invoice payments' : 'to multiple credit memos';

    const transferText =
      otherSideType === 'transfer'
        ? 'Both sides of transfer detected'
        : `This has been applied ${otherSideType === 'multiple-payments' ? multipleCopy : singularCopy}`;

    return (
      <Stack flexDirection={isSplit ? 'column' : 'row'} ml={isSplit ? 2 : 0} mt={isSplit ? 0 : 0.5}>
        <Typography variant='captionRegular'>{transferText}&nbsp;</Typography>
        {otherSideType !== 'multiple-payments' && (
          <Typography variant='captionRegular'>
            <Link
              className={classes.link}
              onClick={otherSideType === 'transfer' ? goToTransfer : viewInvoice}
              id='transfer-other-side-button'
            >
              {otherSideType === 'transfer' ? 'Go to other side' : viewCopy}
            </Link>
          </Typography>
        )}
      </Stack>
    );
  },
);
OtherSideField.displayName = 'OtherSideField';
